import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { analytics, slugify } from '../utils/analytics';
import GamesList from './GamesList';
import GamesHeader from './GamesHeader';
import GamesFilters from './GamesFilters';
import GamesGroupSwitcher from './GamesGroupSwitcher';
import GamesManageGroupsModal from './GamesManageGroupsModal';
import GamesBulkBar from './GamesBulkBar';
import GamesRepairAcross from './GamesRepairAcross';
import PlayOutModal from './PlayOutModal';
import GamesListItemModal from './GamesListItemModal';
import GamesAppliedFilters from './GamesAppliedFilters';
import GamesHeaderTitle from './GamesHeaderTitle';
import { useAppDispatch, useAppSelector } from '../store';
import { setParams } from '../reducers/paramsReducer';
import { getGames, pollGames } from '../reducers/gamesReducer';
import { fetchGroups } from '../reducers/groupsReducer';
import { getFilters } from '../reducers/filtersReducer';
import {
  setGameIdForModal,
  setMoveRefForModal,
  setShowImportGames,
  setShowManageGroups,
} from '../reducers/uiReducer';
import { GroupKind } from '../types/GroupModel';
import AppContext from '../context/AppContext';
import { GamesRequestParams, getGamesUrl, parseGamesQuery } from '../types/GamesRequestParams';
import { readFiltersToRestore, writeSavedFilters } from '../utils/filtersStorage';
import hasFilter from '../utils/hasFilter';
import { fixUrl } from './FixContainer';

export default function GamesContainer({
  library = false,
}: {
  library?: boolean;
}): React.ReactNode {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { search, pathname } = useLocation();
  const { params } = useAppSelector((state) => state.paramsReducer);
  const { games } = useAppSelector((state) => state.gamesReducer);
  const { gameIdForModal } = useAppSelector((state) => state.uiReducer);
  const { groups, loaded: groupsLoaded } = useAppSelector((state) => state.groupsReducer);
  const { isAuthenticated } = useContext(AppContext);
  const [init, setInit] = useState(false);
  // Search stays folded until asked for, or until a filter is set (a shared link, a chip).
  const filtered = hasFilter(params);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    if (filtered) setSearchOpen(true);
  }, [filtered]);
  // At `/` with no filter the page is the trainer above #app (FixOneMove): the list is not drawn.
  const homeTrainer =
    !library && !filtered && document.getElementById('app')?.dataset.homeTrainer === 'true';
  useEffect(() => {
    document.body.classList.toggle('sc-library-view', !homeTrainer);
  }, [homeTrainer]);

  const query = useMemo(() => new URLSearchParams(search), [search]);
  // The URL is the single source of truth for the filters.
  const urlParams = useMemo(() => parseGamesQuery(query), [query]);
  const urlGameId = query.get('gameId') != null ? parseInt(query.get('gameId')!, 10) : null;
  const paramsJson = JSON.stringify(params);

  // URL -> Redux, on load, manual edits and back/forward.
  useEffect(() => {
    // Old links into a repair session or a practice drill go to the trainer.
    const practice = query.get('practice');
    if (query.get('startRepair') === '1' || (urlGameId != null && practice != null)) {
      const target = new URLSearchParams(fixUrl(urlGameId, urlParams).split('?')[1]);
      if (practice != null) target.set('practice', practice);
      navigate(`/fix?${target.toString()}`, { replace: true });
      return;
    }
    // A bare `/games` reopens the filters from last time (see filtersStorage.ts).
    const effective = search === '' && library ? { ...readFiltersToRestore(), page: 0 } : urlParams;
    if (getGamesUrl(effective) !== getGamesUrl(params)) {
      dispatch(setParams(effective));
    }
    if (urlGameId !== gameIdForModal) {
      dispatch(setGameIdForModal(urlGameId));
    }
    // ?gameId=…&move=12w opens the game at that move (links to a note).
    const moveParam = query.get('move');
    if (moveParam) {
      dispatch(setMoveRefForModal(moveParam));
    }
    // Consume-once flags: open the overlay, then strip the flag so it doesn't reopen.
    const openImport = query.get('import') === '1';
    const openGroups = query.get('groups') === '1';
    if (openImport) dispatch(setShowImportGames(true));
    if (openGroups) dispatch(setShowManageGroups(true));
    if (openImport || openGroups) {
      const stripped = new URLSearchParams(search);
      stripped.delete('import');
      stripped.delete('groups');
      const qs = stripped.toString();
      navigate({ search: qs ? `?${qs}` : '' }, { replace: true });
    }
    if (!init) setInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  // Signed in, an address with no group means My Space. Not on the home trainer, which lists
  // nothing and would only gain a `?group=` in its address.
  const needsDefaultGroup = isAuthenticated && params.group == null;
  useEffect(() => {
    if (!groupsLoaded || !needsDefaultGroup || homeTrainer) return;
    const personal = groups.find((g) => g.Kind === GroupKind.Personal);
    if (personal != null) dispatch(setParams({ group: personal.Token }));
  }, [dispatch, groups, groupsLoaded, needsDefaultGroup, homeTrainer]);

  // Redux -> URL.
  useEffect(() => {
    if (!init) return;
    const target = getGamesUrl(params, gameIdForModal);
    if (target !== getGamesUrl(urlParams, urlGameId)) {
      navigate({ pathname, search: target.startsWith('/?') ? target.slice(1) : '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init, paramsJson, gameIdForModal]);

  useEffect(() => {
    if (init) writeSavedFilters(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init, paramsJson]);

  useEffect(() => {
    if (!init || (needsDefaultGroup && !groupsLoaded) || homeTrainer) return;
    dispatch(getGames(params));
    dispatch(getFilters(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init, paramsJson, needsDefaultGroup, groupsLoaded, homeTrainer]);

  // While a listed game is still importing or being evaluated, refresh quietly every few seconds
  // (capped, so a game that never finishes can't poll forever).
  const pollCountRef = useRef(0);
  useEffect(() => {
    pollCountRef.current = 0;
  }, [paramsJson]);
  useEffect(() => {
    if (!init) return;
    const pending = (games?.List ?? []).some(
      (g) => g.MoveCount !== g.MoveTotal || (g.LastEvaluation == null && g.Evaluating),
    );
    if (!pending || pollCountRef.current >= 45) return;
    const id = window.setTimeout(() => {
      pollCountRef.current += 1;
      dispatch(pollGames(params));
    }, 4000);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init, games, paramsJson]);

  // Analytics: the SPA is hash-routed, so GA only sees what we send (docs/02_areas/analytics).
  const prevParamsRef = useRef<GamesRequestParams | null>(null);
  useEffect(() => {
    if (!init) return;
    const prev = prevParamsRef.current;
    prevParamsRef.current = params;
    if (params.opening) {
      analytics.pageview(`/app/opening/${slugify(params.opening)}`, `Opening: ${params.opening}`);
    } else {
      analytics.pageview('/app', 'Games');
    }
    if (prev == null) return;
    const changed = (key: keyof GamesRequestParams) =>
      JSON.stringify(prev[key] ?? null) !== JSON.stringify(params[key] ?? null);
    if (changed('username') && params.username) {
      analytics.track('search', { search_term: params.username });
    }
    if (changed('pgn') && params.pgn) {
      analytics.track('search', { search_term: params.pgn });
    }
    const filterFields: [keyof GamesRequestParams, string][] = [
      ['color', 'color'],
      ['result', 'result'],
      ['platform', 'platform'],
      ['timeControl', 'time_control'],
      ['onlyStarred', 'favorites'],
      ['favoriteMoves', 'favorite_moves'],
      ['hasComments', 'has_comments'],
      ['commentedByMe', 'commented_by_me'],
      ['from', 'date'],
      ['to', 'date'],
    ];
    for (const [key, type] of filterFields) {
      if (changed(key) && params[key] != null) {
        analytics.track('filter_games', { filter_type: type, filter_value: String(params[key]) });
      }
    }
    if (hasFilter(prev) && !hasFilter(params)) {
      analytics.track('clear_filters');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init, paramsJson]);

  const modal = gameIdForModal ? (
    <GamesListItemModal gameId={gameIdForModal} onHide={() => dispatch(setGameIdForModal(null))} />
  ) : null;

  if (homeTrainer) {
    return (
      <div id="container">
        {modal}
        <PlayOutModal />
        <GamesHeader />
      </div>
    );
  }
  return (
    <div id="container">
      {modal}
      <PlayOutModal />
      <GamesHeader />
      <GamesManageGroupsModal />
      <div className="cm-games-top">
        <GamesGroupSwitcher />
        <div className="cm-games-top-end">
          <button
            type="button"
            className={`cm-sync-games-btn${searchOpen ? ' is-active' : ''}`}
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen((o) => !o)}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className="cm-import-prompt-icon" />
            Search
          </button>
          <button
            type="button"
            className="cm-sync-games-btn"
            onClick={() => dispatch(setShowImportGames(true))}
          >
            <FontAwesomeIcon icon={faCloudArrowDown} className="cm-import-prompt-icon" />
            Add games
          </button>
        </div>
      </div>
      {searchOpen ? <GamesFilters /> : null}
      <GamesAppliedFilters />
      <div className="cm-games-actions">
        <GamesHeaderTitle />
        <GamesBulkBar />
        <div className="cm-games-actions-end">
          <GamesRepairAcross />
        </div>
      </div>
      <GamesList />
    </div>
  );
}
